/**
 * useClusteredSignals.js
 *
 * Custom hook for clustering trade signals when zoomed out.
 *
 * WHY THIS EXISTS:
 * - Charts with 4,000+ trade signals become unreadable when zoomed out
 * - Individual arrows overlap and obscure each other
 * - Clustering groups nearby signals into a single marker showing count
 * - Similar to how Google Maps clusters pins when zoomed out
 *
 * HOW IT WORKS:
 * 1. Converts signal timestamps to pixel X positions using xScale
 * 2. Groups signals within clusterRadius pixels of each other
 * 3. Returns clusters with counts (buyCount, sellCount) and original signals
 * 4. When zoomed in enough, clusters naturally break apart to show individuals
 *
 * USAGE:
 * const { clusters, shouldShowClusters } = useClusteredSignals(signals, {
 *   xScale,
 *   clusterRadius: 30,
 * });
 */
import { useMemo } from 'react';

/**
 * Default configuration for clustering
 */
const DEFAULT_CONFIG = {
  clusterRadius: 30,          // Pixels - signals within this distance cluster together
  minClusterSize: 2,          // Minimum signals to form a cluster (below this, show individuals)
  maxIndividualSignals: 200,  // Show individuals if total signals <= this (no clustering needed)
};

/**
 * Cluster signals by pixel proximity.
 *
 * Algorithm:
 * 1. Sort signals by X pixel position
 * 2. Iterate through, grouping consecutive signals within clusterRadius
 * 3. Calculate cluster center and counts
 *
 * @param {Array} signals - Array of signal objects with pixelX property
 * @param {number} clusterRadius - Maximum pixel distance for grouping
 * @returns {Array} Array of cluster objects
 */
function clusterSignalsByProximity(signals, clusterRadius) {
  if (!signals || signals.length === 0) return [];

  // Sort by pixel X position
  const sorted = [...signals].sort((a, b) => a.pixelX - b.pixelX);

  const clusters = [];
  let currentCluster = null;

  for (const signal of sorted) {
    if (!currentCluster) {
      // Start a new cluster
      currentCluster = createCluster(signal);
    } else if (signal.pixelX - currentCluster.rightEdge <= clusterRadius) {
      // Add to current cluster
      addToCluster(currentCluster, signal);
    } else {
      // Finish current cluster and start a new one
      clusters.push(currentCluster);
      currentCluster = createCluster(signal);
    }
  }

  // Don't forget the last cluster
  if (currentCluster) {
    clusters.push(currentCluster);
  }

  return clusters;
}

/**
 * Create a new cluster from a signal
 */
function createCluster(signal) {
  const isBuy = signal.markerType === 'buy';
  const isSell = signal.markerType === 'sell';

  return {
    id: `cluster-${signal.chartTimestamp}`,
    signals: [signal],
    centerX: signal.pixelX,
    centerY: signal.pixelY,
    pixelYSum: signal.pixelY,    // Track pixel Y sum for correct averaging
    leftEdge: signal.pixelX,
    rightEdge: signal.pixelX,
    buyCount: isBuy ? 1 : 0,
    sellCount: isSell ? 1 : 0,
    markerCount: !isBuy && !isSell ? 1 : 0,
    // For zooming to cluster
    startTimestamp: signal.chartTimestamp,
    endTimestamp: signal.chartTimestamp,
    // Average price for Y positioning (data domain, for reference only)
    avgPrice: signal.chartY || 0,
    priceSum: signal.chartY || 0,
  };
}

/**
 * Add a signal to an existing cluster
 */
function addToCluster(cluster, signal) {
  cluster.signals.push(signal);

  // Update edges
  cluster.leftEdge = Math.min(cluster.leftEdge, signal.pixelX);
  cluster.rightEdge = Math.max(cluster.rightEdge, signal.pixelX);

  // Update center X (average of all signal positions)
  cluster.centerX = (cluster.leftEdge + cluster.rightEdge) / 2;

  // Update counts
  const isBuy = signal.markerType === 'buy';
  const isSell = signal.markerType === 'sell';
  if (isBuy) cluster.buyCount++;
  else if (isSell) cluster.sellCount++;
  else cluster.markerCount++;

  // Update timestamps for zoom-to-cluster
  cluster.startTimestamp = Math.min(cluster.startTimestamp, signal.chartTimestamp);
  cluster.endTimestamp = Math.max(cluster.endTimestamp, signal.chartTimestamp);

  // Update average price for Y positioning (data domain)
  cluster.priceSum += signal.chartY || 0;
  cluster.avgPrice = cluster.priceSum / cluster.signals.length;

  // BUGFIX: centerY must stay in pixel domain for SVG rendering.
  // Previously this was set to avgPrice (data domain), causing clusters
  // to render off-screen when they had 2+ signals.
  cluster.pixelYSum += signal.pixelY;
  cluster.centerY = cluster.pixelYSum / cluster.signals.length;
}

/**
 * Custom hook for clustering trade signals based on zoom level.
 *
 * @param {Array} signals - Processed signals with chartTimestamp, chartY, markerType
 * @param {Object} options - Configuration options
 * @param {Function} options.xScale - Recharts x-axis scale function (timestamp -> pixel)
 * @param {Function} options.yScale - Recharts y-axis scale function (price -> pixel)
 * @param {number} options.clusterRadius - Pixel radius for clustering (default: 30)
 * @param {number} options.minClusterSize - Min signals to form cluster (default: 2)
 * @param {number} options.maxIndividualSignals - Max signals before clustering (default: 200)
 * @param {Object} options.zoomRange - { startIndex, endIndex } for visible range
 * @returns {Object} { clusters, individualSignals, shouldShowClusters, stats }
 */
export function useClusteredSignals(signals, options = {}) {
  const {
    xScale,
    yScale,
    clusterRadius = DEFAULT_CONFIG.clusterRadius,
    minClusterSize = DEFAULT_CONFIG.minClusterSize,
    maxIndividualSignals = DEFAULT_CONFIG.maxIndividualSignals,
    zoomRange,
  } = options;

  return useMemo(() => {
    // If no signals or no scale, return empty
    if (!signals || signals.length === 0 || !xScale) {
      return {
        clusters: [],
        individualSignals: [],
        shouldShowClusters: false,
        stats: { totalSignals: 0, clusteredCount: 0, clusterCount: 0 },
      };
    }

    // Filter signals to visible range if zoomRange provided
    let visibleSignals = signals;
    if (zoomRange && zoomRange.startIndex !== null && zoomRange.endIndex !== null) {
      visibleSignals = signals.filter(s =>
        s.chartIndex >= zoomRange.startIndex && s.chartIndex <= zoomRange.endIndex
      );
    }

    // If few enough signals, show individuals (no clustering needed)
    if (visibleSignals.length <= maxIndividualSignals) {
      return {
        clusters: [],
        individualSignals: visibleSignals,
        shouldShowClusters: false,
        stats: {
          totalSignals: visibleSignals.length,
          clusteredCount: 0,
          clusterCount: 0,
        },
      };
    }

    // Add pixel coordinates to each signal
    const signalsWithPixels = visibleSignals.map(signal => ({
      ...signal,
      pixelX: xScale(signal.chartTimestamp),
      pixelY: yScale ? yScale(signal.chartY) : null,
    })).filter(s => !isNaN(s.pixelX)); // Remove signals that can't be positioned

    // Cluster the signals
    const rawClusters = clusterSignalsByProximity(signalsWithPixels, clusterRadius);

    // Separate into clusters (2+ signals) and individuals (1 signal)
    const clusters = [];
    const individualSignals = [];

    for (const cluster of rawClusters) {
      if (cluster.signals.length >= minClusterSize) {
        clusters.push(cluster);
      } else {
        // Single signals become individuals
        individualSignals.push(...cluster.signals);
      }
    }

    // Calculate stats
    const clusteredCount = clusters.reduce((sum, c) => sum + c.signals.length, 0);

    return {
      clusters,
      individualSignals,
      shouldShowClusters: clusters.length > 0,
      stats: {
        totalSignals: visibleSignals.length,
        clusteredCount,
        individualCount: individualSignals.length,
        clusterCount: clusters.length,
      },
    };
  }, [signals, xScale, yScale, clusterRadius, minClusterSize, maxIndividualSignals, zoomRange]);
}

/**
 * Calculate the zoom range needed to show a cluster's signals individually.
 * Used when user clicks a cluster to zoom in.
 *
 * @param {Object} cluster - Cluster object from useClusteredSignals
 * @param {Array} chartData - Full chart data array
 * @param {number} padding - Number of extra data points to include (default: 50)
 * @returns {Object} { startIndex, endIndex } for zooming
 */
export function getClusterZoomRange(cluster, chartData, padding = 50) {
  if (!cluster || !chartData || chartData.length === 0) return null;

  // Find indices for cluster's time range
  const startTime = cluster.startTimestamp;
  const endTime = cluster.endTimestamp;

  let startIndex = 0;
  let endIndex = chartData.length - 1;

  // Binary search for start
  let left = 0;
  let right = chartData.length - 1;
  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    if (chartData[mid].timestamp < startTime) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }
  startIndex = Math.max(0, left - padding);

  // Binary search for end
  left = 0;
  right = chartData.length - 1;
  while (left < right) {
    const mid = Math.floor((left + right + 1) / 2);
    if (chartData[mid].timestamp <= endTime) {
      left = mid;
    } else {
      right = mid - 1;
    }
  }
  endIndex = Math.min(chartData.length - 1, left + padding);

  return { startIndex, endIndex };
}

// Export the clustering function for direct use in Customized components
// where we have access to scales but can't use hooks
export { clusterSignalsByProximity };

export default useClusteredSignals;
